import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ExhaustGasSetting, ExhaustSetting, maxSettingChannels } from 'icreate/common/models/exhaust-gas-setting';
import { AddSettingDialogComponent } from './add-setting-dialog/add-setting-dialog.component';
import { lastValueFrom, take } from 'rxjs';
import { ChannelSearchDialogComponent } from '@components/channel-search-dialog/channel-search-dialog.component';
import { getAnalogTypesSet } from 'icreate/common/models/channel-type';
import { Channel } from 'icreate/common/models/channel';
import { VesselService } from '@service/vessel.service';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-exhaust-setting',
    templateUrl: './exhaust-setting.component.html',
    styleUrl: './exhaust-setting.component.css',
    standalone: false,
})
export class ExhaustSettingComponent {
    @Input() setting: ExhaustGasSetting;
    @Output() exhaustUpdated = new EventEmitter<boolean>();

    @ViewChild('settingChannels') settingChannels: ElementRef;

    isExpanded: boolean = false;
    isExpandDisabled: boolean = false;
    readonly canEditVessel = toSignal(this.vesselService.accessUpdated$, { initialValue: false });

    constructor(
        private cdr: ChangeDetectorRef,
        private dialog: MatDialog,
        private vesselService: VesselService
    ) {}

    ngAfterViewInit() {
        if (this.settingChannels?.nativeElement) {
            this.updateDisableExpand();
        }
    }

    ngOnChanges() {
        this.updateDisableExpand();
    }

    settingIsEmpty(): boolean {
        return !this.setting?.settingChannels || this.setting.settingChannels.length === 0;
    }

    isAddSettingDisabled() {
        return (
            !this.canEditVessel() ||
            !this.setting.settingChannels ||
            this.setting.settingChannels.length >= maxSettingChannels
        );
    }

    async addSetting() {
        if (this.isAddSettingDisabled()) return;
        const newRowDialog = this.dialog.open(AddSettingDialogComponent);
        const newRows = await lastValueFrom(newRowDialog.afterClosed());
        if (newRows) {
            if (newRows == 1) {
                const newIndex = this.setting.settingChannels.length + 1;
                if (newIndex > maxSettingChannels) return;
                const newSettingChannel = new ExhaustSetting(newIndex);
                this.setting.settingChannels.push(newSettingChannel);
                this.rowUpdated(true);
            } else {
                const dialogRef = this.dialog.open(ChannelSearchDialogComponent, {
                    data: {
                        vesselId: this.setting.vesselId,
                        assignedChannel: null,
                        allowedTypesSet: new Set(getAnalogTypesSet()),
                        setShouldDisableTypes: true,
                        multipleSelection: true,
                        preSelectedChannelsCount: this.setting.settingChannels.length,
                    },
                });

                let channelsSelected: Channel[] = await lastValueFrom(dialogRef.afterClosed());

                if (channelsSelected && channelsSelected.length > 0) {
                    channelsSelected = channelsSelected.sort((a, b) =>
                        a.fcuData.fcuChannelId.localeCompare(b.fcuData.fcuChannelId, ['en', 'jp'], {
                            numeric: true,
                            sensitivity: 'variant',
                        })
                    );
                    const newIndex = this.setting.settingChannels.length + 1;
                    channelsSelected.forEach((channel, i) => {
                        if (this.setting.settingChannels.length >= maxSettingChannels) return;
                        const newSetting = new ExhaustSetting(newIndex + i);
                        newSetting.tempChannel = channel.id;
                        newSetting.tempChannelData = channel;
                        this.setting.settingChannels.push(newSetting);
                    });
                    this.rowUpdated(true);
                }
            }
        }
    }

    expandSetting() {
        this.isExpanded = !this.isExpanded;
        this.updateDisableExpand();
    }

    updateDisableExpand() {
        let disabled = false;

        if (this.isExpanded) {
            disabled = false;
        } else if (!this.setting || !this.settingChannels?.nativeElement) {
            disabled = true;
        } else {
            disabled = this.setting.settingChannels.length < 3;
        }

        if (this.isExpandDisabled !== disabled) {
            this.isExpandDisabled = disabled;
            this.cdr.detectChanges();
        }
    }

    duplicateRow(settingRow: ExhaustSetting) {
        if (this.isAddSettingDisabled()) return;
        const newIndex = this.setting.settingChannels.length + 1;
        const newSettingChannel = {
            ...settingRow,
            index: newIndex,
        };
        this.setting.settingChannels.push(newSettingChannel);
        this.rowUpdated(true);
    }

    deleteRow(settingRow: ExhaustSetting) {
        if (!this.setting.settingChannels || this.setting.settingChannels.length == 0) return;
        const index = this.setting.settingChannels.indexOf(settingRow);
        if (!index && index !== 0) return;
        this.setting.settingChannels.splice(index, 1);
        this.setting.settingChannels.sort((a, b) => a.index - b.index);
        this.setting.settingChannels = this.setting.settingChannels.map((setting, i) => {
            setting.index = i + 1;
            return setting;
        });
        this.rowUpdated(true);
    }

    rowUpdated(value: boolean) {
        this.exhaustUpdated.emit(value);
        this.updateDisableExpand();
    }
}
